import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import { theme } from '../../../../theme/theme'
import { BodyLarge, BodySmall, Title3 } from '../../../styles/Typography'
import { Womb } from '../../../icons'

export const EducationalSection = () => {
  const { t } = useTranslation()

  const timeline = [
    { age: t('admin.statistics.lifePhasesAge1'), label: t('admin.statistics.lifePhasesLabel1') },
    { age: t('admin.statistics.lifePhasesAge2'), label: t('admin.statistics.lifePhasesLabel2') },
    { age: t('admin.statistics.lifePhasesAge3'), label: t('admin.statistics.lifePhasesLabel3') },
    { age: t('admin.statistics.lifePhasesAge4'), label: t('admin.statistics.lifePhasesLabel4') },
    { age: t('admin.statistics.lifePhasesAge5'), label: t('admin.statistics.lifePhasesLabel5') },
  ]

  const conditions = [
    t('admin.statistics.lifePhasesCondition1'),
    t('admin.statistics.lifePhasesCondition2'),
    t('admin.statistics.lifePhasesCondition3'),
    t('admin.statistics.lifePhasesCondition4'),
    t('admin.statistics.lifePhasesCondition5'),
  ]

  return (
    <Section>
      <Heading>
        <Trans i18nKey="admin.statistics.lifePhasesTitle" components={{ highlight: <Highlight /> }} />
      </Heading>
      <Intro>{t('admin.statistics.lifePhasesDescription')}</Intro>

      <ExampleCallout>
        <IconWrapper>
          <Womb color={theme.color.orange} size={26} />
        </IconWrapper>
        <BodySmall>{t('admin.statistics.lifePhasesExample')}</BodySmall>
      </ExampleCallout>

      <Timeline>
        <TimelineTrack />
        {timeline.map((point, index) => (
          <TimelineItem key={point.label}>
            <TimelinePoint $position={(index / (timeline.length - 1)) * 100}>
              <Dot $isLast={index === timeline.length - 1} />
              {index !== timeline.length - 1 && <Connector />}
            </TimelinePoint>
            <TimelineText $position={(index / (timeline.length - 1)) * 80} $isLast={index === timeline.length - 1}>
              <Age>{point.age}</Age>
              <PhaseLabel>{point.label}</PhaseLabel>
            </TimelineText>
          </TimelineItem>
        ))}
      </Timeline>

      <FooterBar>
        <FooterLabel>{t('admin.statistics.lifePhasesConditionsLabel')}</FooterLabel>
        <Chips>
          {conditions.map((condition) => (
            <Chip key={condition}>{condition}</Chip>
          ))}
        </Chips>
      </FooterBar>
    </Section>
  )
}

const Section = styled.section`
  background: ${theme.color.beigeDark};
  padding: ${theme.spacing.large}px;
  border-radius: 24px;
  margin-bottom: ${theme.spacing.xxlarge}px;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.large}px ${theme.spacing.medium}px;
  }

  @media print {
    padding: ${theme.spacing.medium}px;
    margin-bottom: 0;
    break-inside: avoid;
  }
`

const Heading = styled(Title3)`
  color: ${theme.color.plum};
  margin-bottom: ${theme.spacing.medium}px;
`

const Highlight = styled.em`
  color: ${theme.color.orange};
  font-style: italic;
`

const Intro = styled(BodyLarge)`
  color: ${theme.color.black};
  max-width: 760px;
  margin-bottom: ${theme.spacing.large}px;

  @media print {
    margin-bottom: ${theme.spacing.medium}px;
  }
`

const ExampleCallout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  background: ${theme.color.white};
  border-radius: 999px;
  padding: 12px 24px;
  margin-bottom: ${theme.spacing.xlarge}px;

  @media print {
    margin-bottom: ${theme.spacing.medium}px;
  }
`

const IconWrapper = styled.div`
  flex-shrink: 0;
`

const TimelineItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: row;
    align-items: stretch;
    gap: ${theme.spacing.small}px;
  }
`
const Timeline = styled.div`
  position: relative;
  height: 90px;
  margin: 0 ${theme.spacing.medium}px ${theme.spacing.xlarge}px;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    height: auto;
    display: flex;
    flex-direction: column;
    margin: 0 0 ${theme.spacing.xlarge}px ${theme.spacing.large}px;
  }

  @media print {
    height: 100px;
    margin-bottom: ${theme.spacing.medium}px;
  }
`

const TimelineTrack = styled.div`
  position: absolute;
  top: 7px;
  left: 0;
  right: 0;
  height: 1px;
  background: ${theme.color.grey3};

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`

const TimelineText = styled.div<{ $position: number; $isLast: boolean }>`
  position: absolute;
  top: 20px;
  left: ${({ $position }) => $position}%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    position: static;
    margin-bottom: ${({ $isLast }) => ($isLast ? 0 : theme.spacing.medium)}px;
  }

  @media print {
    width: 18%;
  }
`
const TimelinePoint = styled.div<{ $position: number }>`
  position: absolute;
  top: 0;
  left: ${({ $position }) => $position}%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    position: static;
    transform: none;
    align-items: center;
    flex-direction: column;
    flex-shrink: 0;
  }
`

const Dot = styled.div<{ $isLast: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $isLast }) => ($isLast ? theme.color.orange : theme.color.plum)};
  margin-bottom: ${theme.spacing.small}px;
  flex-shrink: 0;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    margin-bottom: 0;
  }
`

const Connector = styled.div`
  display: none;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    display: block;
    width: 1px;
    flex: 1;
    background: ${theme.color.grey3};
  }
`

const Age = styled.span`
  color: ${theme.color.orange};
  font-size: ${theme.fontSize.small}px;
  font-family: ${theme.font.body};
`

const PhaseLabel = styled.span`
  font-family: ${theme.font.body};
  font-weight: 500;
  color: ${theme.color.plum};
  font-size: ${theme.fontSize.small}px;
`

const FooterBar = styled.div`
  background: ${theme.color.plum};
  border-radius: 24px;
  padding: ${theme.spacing.large}px;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.large}px;
  flex-wrap: wrap;

  @media print {
    padding: ${theme.spacing.medium}px;
    gap: ${theme.spacing.medium}px;
  }
`

const FooterLabel = styled.span`
  color: ${theme.color.orange};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: ${theme.fontSize.xsmall}px;
  max-width: 280px;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.small}px;
`

const Chip = styled.span`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: ${theme.color.white};
  border-radius: 999px;
  padding: 10px 24px;
  font-size: ${theme.fontSize.small}px;
`
